import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import type { User, AuthState } from '../types';
import api from '../services/api';

const IDLE_TIMEOUT = 30 * 60 * 1000; // 30 minutes
const IDLE_WARNING = 25 * 60 * 1000; // Warning at 25 minutes

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    token: localStorage.getItem('admin_auth_token'),
    isAuthenticated: false,
    isLoading: true,
  });

  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem('admin_auth_token');
    if (!token) {
      setState({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      });
      return;
    }

    try {
      api.setToken(token);
      const user = await api.getProfile();
      
      // Verify user has factory admin access (admin/manager/viewer/production role)
      const allowedRoles = ['admin', 'manager', 'viewer', 'production', 'employee'];
      if (!allowedRoles.includes(user.role) && user.type !== 'employee') {
        throw new Error('Insufficient permissions');
      }
      
      setState({
        user,
        token,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (error) {
      localStorage.removeItem('admin_auth_token');
      api.setToken(null);
      setState({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      });
    }
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const login = async (email: string, password: string) => {
    const { user, token } = await api.login(email, password);

    // Verify user has factory admin or accounting access using unified auth
    // Must be an employee with factory or accounting portal access
    if (user.user_type !== 'employee' && user.user_type !== 'legacy_user') {
      throw new Error('This account does not have access to this portal');
    }

    // Check allowed_portals array - must have factory OR accounting access
    const hasFactoryAccess = user.allowed_portals?.includes('factory');
    const hasAccountingAccess = user.allowed_portals?.includes('accounting');

    if (!hasFactoryAccess && !hasAccountingAccess) {
      throw new Error('This account does not have access to this portal');
    }

    localStorage.setItem('admin_auth_token', token);
    api.setToken(token);
    setState({
      user,
      token,
      isAuthenticated: true,
      isLoading: false,
    });
  };

  const logout = useCallback(() => {
    localStorage.removeItem('admin_auth_token');
    api.setToken(null);
    setState({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
    });
  }, []);

  // Idle timeout — auto-logout after 30 minutes of inactivity
  const idleTimer = useRef<ReturnType<typeof setTimeout>>();
  const warningTimer = useRef<ReturnType<typeof setTimeout>>();
  const [showIdleWarning, setShowIdleWarning] = useState(false);

  useEffect(() => {
    if (!state.isAuthenticated) return;

    const resetIdle = () => {
      setShowIdleWarning(false);
      clearTimeout(idleTimer.current);
      clearTimeout(warningTimer.current);
      warningTimer.current = setTimeout(() => setShowIdleWarning(true), IDLE_WARNING);
      idleTimer.current = setTimeout(() => {
        logout();
        window.location.href = '/login';
      }, IDLE_TIMEOUT);
    };

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];
    events.forEach(e => window.addEventListener(e, resetIdle, { passive: true }));
    resetIdle();

    return () => {
      events.forEach(e => window.removeEventListener(e, resetIdle));
      clearTimeout(idleTimer.current);
      clearTimeout(warningTimer.current);
    };
  }, [state.isAuthenticated, logout]);

  return (
    <AuthContext.Provider value={{ ...state, login, logout, refreshUser }}>
      {children}
      {showIdleWarning && (
        <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm mx-4 text-center">
            <div className="text-4xl mb-3">&#9201;</div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Session Expiring</h3>
            <p className="text-gray-600 text-sm mb-4">You'll be signed out in 5 minutes due to inactivity. Move your mouse or press any key to stay signed in.</p>
            <button
              onClick={() => setShowIdleWarning(false)}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
            >
              Stay Signed In
            </button>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
